import { useCallback, useMemo, useEffect, useRef } from 'react';
import type { FromTypes } from '@/components/funnel/payment/Payment';
import ShareASalePixel from '@/utils/shareASalePixel';
import WebGainsPixel from '@/utils/webGainsPixel';
import { useAppSelector } from './useRedux';

declare global {
    interface Window {
        dataLayer: any;
    }
}

interface IFromProps {
    from: FromTypes;
}

export const useTrackingInit = () => {
    const initialized = useRef(false);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;

            if (typeof window !== 'undefined' && !window.dataLayer) {
                window.dataLayer = [];
            }
        }
    }, []);
};

function useTracking() {
    const planId = useAppSelector((state) => state.plan.currentPlan);
    const totalPrice = useAppSelector((state) => state.booking.totalPrice);
    const bookingTransactionId = useAppSelector((state) => state.booking.transactionId);
    const giftTransactionId = useAppSelector((state) => state.gift.transactionId);
    const { existingDiscount, discountCode } = useAppSelector((state) => state.discount);
    const giftCardQuantity = useAppSelector((state) => state.gift.giftCardAmount);
    const giftCardPrice = useAppSelector((state) => state.gift.giftCardPrice);
    const currencyName = useAppSelector((state) => state.booking.currency.name);
    const email = useAppSelector((state) => state.travellers.adults[0]?.email);
    const phone = useAppSelector((state) => state.travellers.adults[0]?.phone);
    const trackEvents = import.meta.env.VITE_APP_ENV === 'prod';

    const lastTrackedEvents = useRef<Set<string>>(new Set());
    const lastTransactionId = useRef<string>('');

    useEffect(() => {
        const currentTransactionId = (bookingTransactionId || giftTransactionId || '').toString();
        if (currentTransactionId !== lastTransactionId.current) {
            lastTrackedEvents.current.clear();
            lastTransactionId.current = currentTransactionId;
        }
    }, [bookingTransactionId, giftTransactionId]);

    useEffect(() => {
        return () => {
            lastTrackedEvents.current.clear();
        };
    }, []);

    const getProductName = useCallback(
        (from: string) => {
            const isFromGiftCard = from === 'giftCard';
            const isFromGiftTravel = from === 'giftTravel';

            if (isFromGiftCard) return 'Gift Card';
            if (isFromGiftTravel) return `Gift Travel: Plan ${planId}`;
            return `Booking: Plan ${planId}`;
        },
        [planId],
    );

    const getProductCategory = useCallback((from: string) => {
        const isFromGiftCard = from === 'giftCard';
        const isFromGiftTravel = from === 'giftTravel';

        if (isFromGiftCard) return 'Gift Card';
        if (isFromGiftTravel) return `Gift Travel`;
        return `Booking`;
    }, []);

    const trackStartFunnelEvent = useCallback(
        (from: string) => {
            if (!trackEvents) return;

            const eventKey = `start_funnel_${from}_${bookingTransactionId}`;
            if (lastTrackedEvents.current.has(eventKey)) return;

            lastTrackedEvents.current.add(eventKey);

            window.dataLayer.push({
                event: 'add_to_cart',
                transaction_id: bookingTransactionId,
                currency: currencyName,
                value: Number(totalPrice),
                items: [
                    {
                        id: getProductName(from),
                    },
                ],
                email: '',
                phone: '',
            });
        },
        [trackEvents, bookingTransactionId, currencyName, totalPrice, getProductName],
    );

    const trackArrivalToPaymentPage = useCallback(
        ({ from }: IFromProps) => {
            if (!trackEvents) return;

            const isFromBooking = from === 'booking';
            const transactionId = isFromBooking ? bookingTransactionId : giftTransactionId;
            const eventKey = `arrival_payment_${from}_${transactionId}`;

            if (lastTrackedEvents.current.has(eventKey)) return;
            lastTrackedEvents.current.add(eventKey);

            window.dataLayer.push({
                event: 'lead',
                transaction_id: transactionId,
                currency: currencyName,
                value: Number(totalPrice),
                lead_type: 'Arrival to payment page',
            });
        },
        [trackEvents, bookingTransactionId, giftTransactionId, currencyName, totalPrice],
    );

    const trackPurchaseEvent = useCallback(
        ({ from }: IFromProps) => {
            if (!trackEvents) return;

            const isFromBooking = from === 'booking';
            const isFromGiftCard = from === 'giftCard';

            const transactionId = isFromBooking ? bookingTransactionId : giftTransactionId;
            const eventKey = `purchase_${from}_${transactionId}`;

            if (lastTrackedEvents.current.has(eventKey)) return;
            lastTrackedEvents.current.add(eventKey);

            ShareASalePixel.trackPurchase(totalPrice, transactionId, currencyName, discountCode);
            WebGainsPixel.trackPurchase(totalPrice, transactionId, currencyName, discountCode);

            window.dataLayer.push({
                event: 'purchase',
                transaction_id: transactionId,
                value: Number(totalPrice),
                currency: currencyName,
                ...(existingDiscount && { coupon: discountCode }),
                items: [
                    {
                        id: getProductName(from),
                        item_name: getProductName(from),
                        item_category: getProductCategory(from),
                        price: isFromGiftCard ? `${giftCardPrice}` : `${totalPrice}`,
                        quantity: isFromGiftCard ? giftCardQuantity : 1,
                    },
                ],
                email,
                phone: `+${phone}`,
            });
        },
        [
            trackEvents,
            bookingTransactionId,
            giftTransactionId,
            totalPrice,
            currencyName,
            discountCode,
            existingDiscount,
            getProductName,
            getProductCategory,
            giftCardPrice,
            giftCardQuantity,
            email,
            phone,
        ],
    );

    return {
        trackPurchaseEvent,
        trackStartFunnelEvent,
        trackArrivalToPaymentPage,
    };
}

export default useTracking;
